/**
 * Vault recovery requests (Phase 9c). Approving (with a fresh 2FA code) starts the waiting period;
 * the restore itself is done later on the OWNER'S PC with `pnpm admin:recover-vault`, because the
 * recovery key is never on the server. Check the user's identity BEFORE approving.
 */
import type { AdminVaultRecoveryRequest } from "@custos/api-client";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Notice,
  Segmented,
  Skeleton,
  Spot,
  formatDateTime,
  type Tone,
} from "@custos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { useState } from "react";
import { PageHeader } from "../components/AdminShell";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { useCan } from "../lib/permissions";
import { KeyFileCard, QuickRevealCard, RevealModal } from "./OwnerReveal";

type Filter = "OPEN" | "ALL";

const TONE: Record<AdminVaultRecoveryRequest["status"], Tone> = {
  REQUESTED: "warning",
  APPROVED: "primary",
  READY: "primary",
  COMPLETED: "success",
  CANCELLED: "neutral",
  REJECTED: "danger",
};

const NEXT: Record<AdminVaultRecoveryRequest["status"], string> = {
  REQUESTED: "Check the user's identity, then approve or reject.",
  APPROVED:
    "Approved. Either read the words and tell the client over a trusted line (owner), or after the waiting period run the restore command on your PC.",
  READY: "Restored for the user's device — waiting for them to finish in the app.",
  COMPLETED: "Done.",
  CANCELLED: "Cancelled by the user.",
  REJECTED: "Rejected.",
};

export function VaultRecoveryPage() {
  const can = useCan();
  const canDecide = can("vault_recovery.decide");
  const canReveal = can("vault_recovery.reveal");
  const [revealFor, setRevealFor] = useState<AdminVaultRecoveryRequest | null>(null);
  const [filter, setFilter] = useState<Filter>("OPEN");
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["admin", "vault-recovery", filter],
    queryFn: () => api.vaultRecoveryRequests(filter === "ALL" ? "ALL" : ""),
    refetchInterval: 30_000,
  });
  const [action, setAction] = useState<{ kind: "approve" | "reject"; r: AdminVaultRecoveryRequest } | null>(
    null,
  );
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["admin", "vault-recovery"] });
  const now = Date.now();

  return (
    <>
      <PageHeader title="Vault recovery" />
      <Notice tone="warning" title="Before approving">
        Make sure it's really the user (e.g. contact them on a known channel, ask about their account).
        Scammers pretend to have "lost their phone". Approval starts a waiting period; the user is emailed and
        can cancel.
      </Notice>
      {canReveal && (
        <div className="mt-4">
          <QuickRevealCard />
          <KeyFileCard />
        </div>
      )}
      <div className="my-4">
        <Segmented<Filter>
          label="Show"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "OPEN", label: "Open" },
            { value: "ALL", label: "All" },
          ]}
        />
      </div>
      {list.isPending ? (
        <Skeleton className="h-40 rounded-card" />
      ) : list.error ? (
        <Notice tone="danger">{errorMessage(list.error)}</Notice>
      ) : list.data.length === 0 ? (
        <Card>
          <EmptyState icon={<Spot name="vault" />} title="No recovery requests" />
        </Card>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="vault-recovery-list">
          {list.data.map((r) => {
            const waitOver = r.readyAt !== null && new Date(r.readyAt).getTime() <= now;
            return (
              <li key={r.id}>
                <Card className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/users/${r.userId}`} className="font-medium text-fg hover:underline">
                      {r.email}
                    </Link>
                    <Badge tone={TONE[r.status]}>{r.status}</Badge>
                  </div>
                  <p className="text-sm text-muted">
                    Requested {formatDateTime(r.requestedAt)} · user since {formatDateTime(r.userSince)}
                    {r.userStatus !== "ACTIVE" && ` · account ${r.userStatus}`}
                  </p>
                  {r.readyAt && (
                    <p className="text-sm text-muted">Waiting period ends {formatDateTime(r.readyAt)}</p>
                  )}
                  <p className="text-sm text-fg">{NEXT[r.status]}</p>
                  {r.status === "APPROVED" && waitOver && (
                    <div className="rounded-field border border-line bg-soft p-3 text-sm">
                      <p className="text-muted">On your PC (PowerShell, project folder):</p>
                      <code className="mt-1 block font-mono text-xs break-all text-fg">
                        pnpm admin:recover-vault {r.id}
                      </code>
                    </div>
                  )}
                  {(canDecide || canReveal) && ["REQUESTED", "APPROVED", "READY"].includes(r.status) && (
                    <div className="flex flex-wrap gap-2">
                      {canReveal && (r.status === "APPROVED" || r.status === "READY") && (
                        <Button size="sm" onClick={() => setRevealFor(r)}>
                          Read words (owner)
                        </Button>
                      )}
                      {canDecide && r.status === "REQUESTED" && (
                        <Button size="sm" onClick={() => setAction({ kind: "approve", r })}>
                          Approve
                        </Button>
                      )}
                      {canDecide && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setAction({ kind: "reject", r })}
                        >
                          Reject
                        </Button>
                      )}
                    </div>
                  )}
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <RevealModal request={revealFor} onClose={() => setRevealFor(null)} />
      <StepUpModal
        open={action?.kind === "approve"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Approve vault recovery"
        description={action ? `For ${action.r.email}. Only approve after checking their identity.` : ""}
        confirmLabel="Approve and start the waiting period"
        onConfirm={(code) => api.approveVaultRecovery(action!.r.id, code)}
        onDone={refresh}
      />
      <StepUpModal
        open={action?.kind === "reject"}
        onOpenChange={(o) => !o && setAction(null)}
        title="Reject vault recovery"
        description={action ? `The request of ${action.r.email} is closed; they are emailed.` : ""}
        confirmLabel="Reject"
        danger
        onConfirm={(code) => api.rejectVaultRecovery(action!.r.id, code)}
        onDone={refresh}
      />
    </>
  );
}
